import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_TABLE_SETTINGS,
  visibleTableColumns,
  type IssueSortField,
  type IssueSummary,
  type Me,
  type SortDirection,
  type TableColumn,
} from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import {
  filtersQuery,
  issuesQuery,
  projectsQuery,
  syncStatusQuery,
  useAddIssueToPlanMutation,
  useSyncNow,
} from '../api/queries.js';
import { AppHeader } from '../components/AppHeader.js';
import { ColumnsMenu } from '../components/ColumnsMenu.js';
import { CreateIssueModal } from '../components/CreateIssueModal.js';
import { CreateSectionModal } from '../components/CreateSectionModal.js';
import { FilterPopover } from '../components/FilterPopover.js';
import { IssuesTable } from '../components/IssuesTable.js';

import { Sidebar, type AppView } from '../components/Sidebar.js';
import { SyncBar, SyncProgress } from '../components/SyncProgress.js';
import { IssueModal } from '../components/IssueModal.js';
import { LinksPage } from './LinksPage.js';
import { MeetingsPage } from './MeetingsPage.js';
import { PlanningPage } from './PlanningPage.js';
import { SettingsPage, type SettingsSection } from './SettingsPage.js';
import { EmptyState, ErrorCard, RevokedState, TableSkeleton } from '../components/States.js';
import { useDebounced } from '../lib/useDebounced.js';
import { useTableSettings } from '../lib/useTableSettings.js';
import styles from './IssuesPage.module.css';

/**
 * Заметки тянут за собой редактор ProseMirror — треть всего бандла. Загружаются отдельным
 * куском при первом переключении на них: таблицу открывают все, редактор — не каждый.
 */
const NotesPage = lazy(() =>
  import('./NotesPage.js').then((module) => ({ default: module.NotesPage })),
);

/** Значение фильтра исполнителя, обозначающее задачи без назначенного. */
const UNASSIGNED = '__unassigned__';

/** Виды раздела в том порядке, в каком они стоят в тулбаре. Ключ локали совпадает с именем. */
const SECTION_VIEWS = ['issues', 'notes', 'links', 'meetings'] as const;
type SectionView = (typeof SECTION_VIEWS)[number];

/**
 * Главный экран: разделы слева, таблица задач справа.
 *
 * Данные читаются из кэша приложения, а не напрямую из Jira: только так работают сортировка
 * по любой колонке и устойчивая курсорная прокрутка. Обновление кэша идёт фоном, поэтому
 * пустой ответ в первые секунды после подключения — это не ошибка, а ещё не доехавшая
 * синхронизация.
 */
export function IssuesPage({ me }: { me: Me }): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [projectId, setProjectId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<string[]>([]);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [priorities, setPriorities] = useState<string[]>([]);
  const [sort, setSort] = useState<IssueSortField>('updated');
  const [dir, setDir] = useState<SortDirection>('desc');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [openedKey, setOpenedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /**
   * Что показывает раздел: задачи из Jira, заметки и ссылки Jisy или встречи этого направления.
   * Вид принадлежит разделу, а не приложению, поэтому переключатель стоит в тулбаре, а не
   * в боковой колонке рядом с «Таблицей» и «Планированием».
   */
  const [sectionView, setSectionView] = useState<SectionView>('issues');
  // Нажали «Обновить» — только тогда показываем ход работы над непустой таблицей.
  const [manualSync, setManualSync] = useState(false);
  // Настройки занимают весь экран целиком, поэтому они стоят в том же переключателе вида,
  // хотя в боковой колонке их нет: туда ведёт кнопка в шапке.
  const [view, setView] = useState<AppView | 'settings'>('table');
  // С какого раздела открыть настройки: из встреч ведут прямо к ключу Fathom, а не к Jira.
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('integration');
  // На планшете боковая колонка по умолчанию свёрнута: иначе тулбару не хватает ширины
  // и переключатели уезжают за край. Развернуть её можно кнопкой, как на широком экране.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => window.matchMedia('(max-width: 1279px)').matches,
  );

  // «Все разделы» — не направление: заметки, ссылки и встречи там показывать не от чего.
  useEffect(() => {
    if (projectId === null && sectionView !== 'issues') setSectionView('issues');
  }, [projectId, sectionView]);

  // Поиск не должен слать запрос на каждую букву.
  const debouncedSearch = useDebounced(search, 300);

  // Колонки и показ завершённых — личные настройки пользователя, они переживают перезаход.
  const { settings: table, update: updateTable } = useTableSettings();

  const projects = useQuery(projectsQuery);
  const filters = useQuery(filtersQuery(projectId));
  const sync = useQuery(syncStatusQuery);
  const syncNow = useSyncNow();
  const addToPlan = useAddIssueToPlanMutation();
  const [creatingSection, setCreatingSection] = useState(false);

  /**
   * Выбранный раздел для шапки: она показывает ссылку на его проект в Jira. В планировании,
   * встречах и настройках разделов в боковой колонке нет, поэтому и ссылки быть не должно.
   */
  const headerProject = useMemo(
    () =>
      view === 'table' && projectId !== null
        ? ((projects.data?.projects ?? []).find((item) => item.id === projectId) ?? null)
        : null,
    [projectId, projects.data, view],
  );

  // Объект фильтра входит в ключ запроса: собираем его один раз на набор значений,
  // иначе каждый перерисовыв компонента выглядел бы для кэша как новый запрос.
  const filter = useMemo(
    () => ({
      projectId: projectId ?? undefined,
      q: debouncedSearch,
      status: statuses,
      assignee: assignees.filter((id) => id !== UNASSIGNED),
      unassigned: assignees.includes(UNASSIGNED),
      type: types,
      priority: priorities,
      doneOnly: table.doneOnly,
      // Подзадачи едут под своими задачами, как в списках Jira, а не россыпью по таблице.
      tree: true,
      sort,
      dir,
    }),
    [projectId, debouncedSearch, statuses, assignees, types, priorities, table.doneOnly, sort, dir],
  );

  const issues = useInfiniteQuery(issuesQuery(filter));

  const rows = useMemo(
    () => issues.data?.pages.flatMap((page) => page.items) ?? [],
    [issues.data],
  );

  const firstPage = issues.data?.pages[0];
  const total = firstPage?.total ?? 0;
  // Сервер считает задачи, а не строки: подзадачи под родителями входят в его число.
  const loaded = useMemo(
    () => rows.reduce((sum, row) => sum + 1 + row.children.length, 0),
    [rows],
  );
  const filtersActive =
    statuses.length > 0 ||
    assignees.length > 0 ||
    types.length > 0 ||
    priorities.length > 0 ||
    debouncedSearch.trim().length > 0;

  /*
   * Показываем ход синхронизации только там, где его ждут: первая загрузка (таблице всё равно
   * нечего показать) и нажатие «Обновить». Регулярное фоновое обновление кэша пользователя не
   * касается — оно идёт при каждом заходе после истечения TTL, и полоса мелькала бы впустую.
   */
  const running = sync.data?.running === true;
  const firstRun = sync.data?.firstRun === true;

  // С включёнными фильтрами пусто из-за них, а не из-за загрузки — там обычное «ничего не найдено».
  const showFirstRun = (running || firstRun) && rows.length === 0 && !filtersActive;
  const showManualBar = running && manualSync && rows.length > 0;
  const syncing = showFirstRun || (running && manualSync);

  /*
   * Синхронизация закончилась — перечитываем таблицу и разделы. Сами по себе они этого не
   * узнают: запрос задач уже отработал и вернул пустую страницу, а его интервал обновления
   * рассчитан на обычную работу, а не на первую загрузку.
   */
  const wasSyncing = useRef(false);
  useEffect(() => {
    if (wasSyncing.current && !running) {
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['filters'] });
      setManualSync(false);
    }
    wasSyncing.current = running;
  }, [running, queryClient]);

  /*
   * Списки значений считаются по задачам раздела, поэтому при его смене часть выбранного может
   * исчезнуть. Такой выбор снимаем: таблица осталась бы пустой, а причину не увидеть — значения
   * нет ни в одном списке, снять галку негде.
   */
  useEffect(() => {
    const data = filters.data;
    if (!data) return;

    const keep =
      (available: string[]) =>
      (current: string[]): string[] => {
        // «Без исполнителя» в справочник не приходит и доступно всегда.
        const next = current.filter((id) => id === UNASSIGNED || available.includes(id));
        return next.length === current.length ? current : next;
      };

    setStatuses(keep(data.statuses.map((status) => status.id)));
    setTypes(keep(data.issueTypes.map((type) => type.id)));
    setPriorities(keep(data.priorities.map((priority) => priority.id)));
    setAssignees(keep(data.assignees.map((person) => person.accountId)));
  }, [filters.data]);

  const resetFilters = useCallback(() => {
    setStatuses([]);
    setAssignees([]);
    setTypes([]);
    setPriorities([]);
    setSearch('');
    // Отбор завершённых — тоже фильтр: сброс возвращает таблицу к обычному виду.
    updateTable((current) => ({ ...current, doneOnly: false }));
  }, [updateTable]);

  const columns = useMemo(
    () => visibleTableColumns(table, { showSection: projectId === null }),
    [table, projectId],
  );

  const handleResizeColumn = useCallback(
    (column: TableColumn, width: number) => {
      updateTable((current) => ({ ...current, widths: { ...current.widths, [column]: width } }));
    },
    [updateTable],
  );

  /** Перенос заголовка мышью: колонка встаёт на место той, на которую её бросили. */
  const handleMoveColumn = useCallback(
    (column: TableColumn, target: TableColumn) => {
      updateTable((current) => {
        const from = current.order.indexOf(column);
        const to = current.order.indexOf(target);
        if (from === -1 || to === -1) return current;

        const order = current.order.filter((item) => item !== column);
        const at = order.indexOf(target);
        // Слева направо колонка встаёт за целевой, справа налево — перед ней: так она
        // оказывается там, куда указывает подсветка.
        order.splice(from < to ? at + 1 : at, 0, column);
        return { ...current, order };
      });
    },
    [updateTable],
  );

  /** То же самое кнопками в меню — единственный способ переставить колонку с клавиатуры. */
  const handleShiftColumn = useCallback(
    (column: TableColumn, direction: -1 | 1) => {
      updateTable((current) => {
        const from = current.order.indexOf(column);
        const to = from + direction;
        if (from === -1 || to < 0 || to >= current.order.length) return current;

        const order = [...current.order];
        const [moved] = order.splice(from, 1);
        if (moved) order.splice(to, 0, moved);
        return { ...current, order };
      });
    },
    [updateTable],
  );

  const handleToggleColumn = useCallback(
    (column: TableColumn) => {
      updateTable((current) => ({
        ...current,
        hidden: current.hidden.includes(column)
          ? current.hidden.filter((item) => item !== column)
          : [...current.hidden, column],
      }));
    },
    [updateTable],
  );

  /** Сброс возвращает вид таблицы к макету, но не трогает показ завершённых: это фильтр. */
  const handleResetColumns = useCallback(() => {
    updateTable((current) => ({ ...DEFAULT_TABLE_SETTINGS, doneOnly: current.doneOnly }));
  }, [updateTable]);

  /** Повторное нажатие по той же колонке переворачивает порядок. */
  const handleSort = useCallback(
    (field: IssueSortField) => {
      if (field === sort) {
        setDir((current) => (current === 'asc' ? 'desc' : 'asc'));
        return;
      }
      setSort(field);
      // Даты удобнее смотреть от свежих, остальное — по алфавиту с начала.
      setDir(field === 'updated' || field === 'duedate' ? 'desc' : 'asc');
    },
    [sort],
  );

  // Отозванный доступ — отдельное состояние: планы целы, нужно лишь переподключиться.
  if (me.tenant.status !== 'active') {
    return (
      <div className={styles.page}>
        <AppHeader
          me={me}
          project={null}
          onOpenSettings={() => setView('settings')}
          onOpenIssue={setOpenedKey}
        />
        <div className={styles.shell}>
          <RevokedState onReconnect={() => window.location.assign('/connect')} />
        </div>
      </div>
    );
  }

  const error = (issues.error ?? projects.error) as ApiRequestError | null;

  /**
   * Виды раздела. Показываются только для выбранного раздела: у режима «Все разделы»
   * направления нет, а всё, кроме задач, принадлежит именно ему.
   */
  const sectionSwitch =
    projectId === null ? null : (
      <div className={styles.segmented} role="group" aria-label={t('sectionView.label')}>
        {SECTION_VIEWS.map((view) => (
          <button
            type="button"
            key={view}
            className={sectionView === view ? styles.segmentActive : styles.segment}
            onClick={() => setSectionView(view)}
            aria-pressed={sectionView === view}
          >
            {t(`sectionView.${view}`)}
          </button>
        ))}
      </div>
    );

  return (
    <div className={styles.page}>
      <AppHeader
        me={me}
        project={headerProject}
        onOpenSettings={() => setView('settings')}
        onOpenIssue={setOpenedKey}
      />

      <div className={styles.shell}>
        {view === 'settings' ? (
          <SettingsPage
            isAdmin={me.user.role === 'admin'}
            initialSection={settingsSection}
            onClose={() => setView('table')}
          />
        ) : (
          <>
            <Sidebar
              view={view}
              onChangeView={setView}
              projects={projects.data?.projects ?? []}
              totalIssues={projects.data?.total ?? 0}
              selectedProjectId={projectId}
              onSelect={(id) => {
                setProjectId(id);
                setSelectedKey(null);
              }}
              lastSyncAt={sync.data?.lastSyncAt ?? projects.data?.lastSyncAt ?? null}
              syncing={syncing}
              onSyncNow={() => {
                setManualSync(true);
                syncNow.mutate();
              }}
              onCreateSection={() => setCreatingSection(true)}
              collapsed={sidebarCollapsed}
              onToggleCollapsed={() => setSidebarCollapsed((value) => !value)}
            />

            {view === 'planning' ? (
              <PlanningPage siteUrl={me.tenant.siteUrl} onOpenIssue={setOpenedKey} />
            ) : view === 'meetings' ? (
              <MeetingsPage
                onOpenSettings={() => {
                  setSettingsSection('meetings');
                  setView('settings');
                }}
              />
            ) : sectionView === 'meetings' && projectId !== null ? (
              /* Тот же экран встреч, что и в боковой колонке, но с отбором по направлению:
                 вкладка отвечает на вопрос «о чём мы говорили по этому разделу». */
              <MeetingsPage
                projectId={projectId}
                leading={sectionSwitch}
                onOpenSettings={() => {
                  setSettingsSection('meetings');
                  setView('settings');
                }}
              />
            ) : sectionView === 'links' && projectId !== null ? (
              <main className={styles.main}>
                <div className={styles.toolbar}>
                  {sectionSwitch}
                  <div className={styles.spacer} />
                </div>
                <LinksPage projectId={projectId} />
              </main>
            ) : sectionView === 'notes' && projectId !== null ? (
              <main className={styles.main}>
                <div className={styles.toolbar}>
                  {sectionSwitch}
                  <div className={styles.spacer} />
                </div>
                <Suspense fallback={<div className={styles.notesLoading} />}>
                  <NotesPage projectId={projectId} />
                </Suspense>
              </main>
            ) : (
              <main className={styles.main}>
                <div className={styles.toolbar}>
                  {sectionSwitch}
                  <div className={styles.searchField}>
                    <span className={styles.searchIcon} aria-hidden="true">
                      ⌕
                    </span>
                    <input
                      className={styles.searchInput}
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder={t('table.search')}
                      aria-label={t('table.search')}
                    />
                  </div>

                  <FilterPopover
                    name={t('filters.status')}
                    options={(filters.data?.statuses ?? []).map((status) => ({
                      id: status.id,
                      label: status.name,
                    }))}
                    selected={statuses}
                    onChange={setStatuses}
                    searchable
                  />
                  <FilterPopover
                    name={t('filters.assignee')}
                    options={(filters.data?.assignees ?? []).map((person) => ({
                      id: person.accountId,
                      label: person.displayName,
                    }))}
                    selected={assignees}
                    onChange={setAssignees}
                    searchable
                    extraOption={{ id: UNASSIGNED, label: t('filters.unassigned') }}
                  />
                  <FilterPopover
                    name={t('filters.type')}
                    options={(filters.data?.issueTypes ?? []).map((type) => ({
                      id: type.id,
                      label: type.name,
                    }))}
                    selected={types}
                    onChange={setTypes}
                    searchable
                  />
                  <FilterPopover
                    name={t('filters.priority')}
                    options={(filters.data?.priorities ?? []).map((priority) => ({
                      id: priority.id,
                      label: priority.name,
                      hint: priority.rank === null ? undefined : `P${priority.rank}`,
                    }))}
                    selected={priorities}
                    onChange={setPriorities}
                  />

                  {/* Обычно завершённых в таблице нет; нажатый переключатель оставляет только их. */}
                  <button
                    type="button"
                    className={table.doneOnly ? styles.toggleActive : styles.toggle}
                    onClick={() =>
                      updateTable((current) => ({ ...current, doneOnly: !current.doneOnly }))
                    }
                    aria-pressed={table.doneOnly}
                    title={t('table.doneOnlyHint')}
                  >
                    {t('table.doneOnly')}
                  </button>

                  <div className={styles.spacer} />

                  <ColumnsMenu
                    settings={table}
                    onToggle={handleToggleColumn}
                    onMove={handleShiftColumn}
                    onReset={handleResetColumns}
                  />

                  <button
                    type="button"
                    className={styles.createButton}
                    onClick={() => setCreating(true)}
                    // Без разделов создавать негде: список ещё не доехал или у пользователя нет проектов.
                    disabled={(projects.data?.projects.length ?? 0) === 0}
                  >
                    {t('create.action')}
                  </button>
                </div>

                {error && (
                  <ErrorCard
                    error={error}
                    cachedAt={firstPage?.freshness.fetchedAt ?? null}
                    onRetry={() => {
                      void queryClient.invalidateQueries({ queryKey: ['issues'] });
                      void queryClient.invalidateQueries({ queryKey: ['projects'] });
                    }}
                  />
                )}

                {/*
                  Синхронизация при непустой таблице — например, по кнопке «Обновить». Прятать за
                  индикатором готовые строки незачем, поэтому ход работы идёт полосой над ними.
                */}
                {showManualBar && sync.data && <SyncBar status={sync.data} />}

                {issues.isPending && !error && <TableSkeleton />}

                {/*
                  Первая загрузка тенанта идёт минуты. Пока в кэше пусто, а синхронизация работает,
                  на месте таблицы виден её ход — иначе пустой экран читается как поломка.
                */}
                {!issues.isPending && !error && showFirstRun && sync.data && (
                  <SyncProgress status={sync.data} />
                )}

                {!issues.isPending && rows.length === 0 && !error && !showFirstRun && (
                  <EmptyState
                    filtered={filtersActive || table.doneOnly}
                    onResetFilters={resetFilters}
                    onShowAll={() => {
                      resetFilters();
                      setProjectId(null);
                    }}
                  />
                )}

                {rows.length > 0 && (
                  <IssuesTable
                    issues={rows}
                    columns={columns}
                    widths={table.widths}
                    onResizeColumn={handleResizeColumn}
                    onMoveColumn={handleMoveColumn}
                    sort={sort}
                    dir={dir}
                    onSort={handleSort}
                    selectedKey={selectedKey}
                    onSelect={setSelectedKey}
                    onOpen={(issue: IssueSummary) => {
                      setSelectedKey(issue.key);
                      setOpenedKey(issue.key);
                    }}
                    // mutateAsync, а не mutate: таблица ставит отметку сразу и снимает её,
                    // если сервер откажет.
                    onAddToPlan={(issue: IssueSummary) => addToPlan.mutateAsync(issue.key)}
                    onLoadMore={() => {
                      if (issues.hasNextPage && !issues.isFetchingNextPage)
                        void issues.fetchNextPage();
                    }}
                    hasMore={issues.hasNextPage}
                    loadingMore={issues.isFetchingNextPage}
                  />
                )}

                <div className={styles.footer}>
                  {/*
                    Пока загружено всё, что нашлось, «показано» и «всего» — одно и то же число,
                    и разница появляется только при догрузке прокруткой. Поэтому обычно здесь
                    просто счёт найденного, а «N из M» включается, когда хвост ещё не подтянут.
                  */}
                  <span className={styles.footerText}>
                    {loaded < total
                      ? t('table.shown', { shown: loaded, total })
                      : t('table.found', { total })}
                  </span>
                  <div className={styles.spacer} />
                  <span className={`mono ${styles.hints}`}>{t('table.hints')}</span>
                </div>
              </main>
            )}
          </>
        )}
      </div>

      {creating && (
        <CreateIssueModal
          projects={projects.data?.projects ?? []}
          initialProjectId={projectId}
          siteUrl={me.tenant.siteUrl}
          onClose={() => setCreating(false)}
          onCreated={(issue) => {
            setCreating(false);
            /*
             * Строка выделяется, но карточка не открывается: всё, что в ней показали бы,
             * человек только что сам и заполнил. Выделение остаётся полезным — новая задача
             * может не попасть в текущий фильтр, и по нему видно, что вышло.
             */
            setSelectedKey(issue.key);
          }}
        />
      )}

      {creatingSection && (
        <CreateSectionModal
          onClose={() => setCreatingSection(false)}
          onCreated={(project) => {
            setCreatingSection(false);
            // Раздел заводят, чтобы в него складывать задачи, — сразу открываем его в таблице.
            setProjectId(project.id);
            setSelectedKey(null);
          }}
        />
      )}

      {openedKey && (
        <IssueModal
          // Переход к родителю или подзадаче — это другая карточка: правки, начатые в прежней,
          // за ней не тянутся.
          key={openedKey}
          issueKey={openedKey}
          siteUrl={me.tenant.siteUrl}
          onClose={() => setOpenedKey(null)}
          onOpenIssue={(key) => {
            setOpenedKey(key);
            setSelectedKey(key);
          }}
        />
      )}
    </div>
  );
}

